import axios from 'axios';
import { parseProblem } from '@shared/api-errors';
import { getCorrelationId } from '../lib/correlation';
import { logger } from '../lib/logger';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and correlation ID
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('saas_admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Correlation-ID'] = getCorrelationId();
  return config;
});

// Shared refresh promise to prevent concurrent refresh calls
let refreshPromise: Promise<string> | null = null;

function clearAdminAuthAndRedirect() {
  localStorage.removeItem('saas_admin_token');
  localStorage.removeItem('saas_admin_refresh_token');
  localStorage.removeItem('saas_admin_user');
  window.location.href = '/login';
}

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Login isteğinin kendi 401'i buraya düşmemeli: redirect tam sayfa reload
    // yapıp Login formundaki hata state'ini siliyordu — kullanıcı "geçersiz
    // şifre" mesajını hiç göremiyordu. Refresh isteğinin 401'i de düşmemeli
    // (sonsuz döngü).
    const isAuthRequest =
      originalRequest?.url?.includes('/saas-admin/auth/login') ||
      originalRequest?.url?.includes('/saas-admin/auth/refresh-token');

    if (error.response?.status === 401 && !isAuthRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('saas_admin_refresh_token');
      if (!refreshToken) {
        clearAdminAuthAndRedirect();
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          // Bare axios: api instance'ı kullanmak interceptor'ları yeniden tetiklerdi
          refreshPromise = axios
            .post(`${api.defaults.baseURL}/saas-admin/auth/refresh-token`, { refreshToken })
            .then((res) => {
              const { accessToken, refreshToken: newRefreshToken, user } = res.data;
              localStorage.setItem('saas_admin_token', accessToken);
              localStorage.setItem('saas_admin_refresh_token', newRefreshToken);
              if (user) localStorage.setItem('saas_admin_user', JSON.stringify(user));
              return accessToken;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const accessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        clearAdminAuthAndRedirect();
        return Promise.reject(error);
      }
    }

    // Normalize to ApiError when the server sent problem+json (or a legacy
    // {message,errors} 4xx shape — see parseProblem fallback). Downstream
    // consumers (toastFromApiError, mapProblemToFormErrors, mutationCache
    // onError) get a uniform shape regardless of the backend endpoint's
    // migration status.
    const apiErr = parseProblem(error);
    if (apiErr) return Promise.reject(apiErr);

    logger.warn('API request failed', {
      url: error.config?.url,
      status: error.response?.status,
      correlationId: getCorrelationId(),
    });

    return Promise.reject(error);
  }
);

export default api;
